import React, { useState, useEffect } from 'react';
import { Tags, Search, AlertTriangle, Loader, Replace, Trash2, X, Edit2 } from 'lucide-react';
import { tagService, TagWithCounts } from '../../lib/tagService';
import { useAuth } from '../../hooks/useAuth';

interface ReplaceTagModalProps {
  isOpen: boolean;
  tag: TagWithCounts | null;
  allTags: TagWithCounts[];
  onClose: () => void;
  onSuccess: () => void;
}

const ReplaceTagModal: React.FC<ReplaceTagModalProps> = ({ isOpen, tag, allTags, onClose, onSuccess }) => {
  const { appUser } = useAuth();
  const [selectedTag, setSelectedTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editedName, setEditedName] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'replace'>('edit');
  const [error, setError] = useState('');

  useEffect(() => {
    if (tag) {
      setSelectedTag('');
      setSearchTerm('');
      setEditedName(tag.tag);
      setActiveTab('edit');
    }
  }, [tag]);

  // Filter tags for dropdown (exclude current tag)
  const availableTags = allTags.filter(t => 
    t.tag !== tag?.tag &&
    t.tag.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10); // Limit to 10 results

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tag || !appUser) return;

    if (!editedName.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    if (editedName.trim() === tag.tag) {
      setError('Please enter a different name');
      return;
    }

    // Check if the new name already exists
    if (allTags.some(t => t.tag.toLowerCase() === editedName.trim().toLowerCase() && t.tag !== tag.tag)) {
      setError('A tag with this name already exists');
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await tagService.replaceTag(tag.tag, editedName.trim(), appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Updated ${result.updated} items, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to edit tag name');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleReplace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tag || !appUser) return;

    if (!selectedTag.trim()) {
      setError('Please select a tag to replace with');
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await tagService.replaceTag(tag.tag, selectedTag, appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Updated ${result.updated} items, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to replace tag');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleDelete = async () => {
    if (!tag || !appUser) return;

    if (!window.confirm(`Are you sure you want to delete the tag "${tag.tag}"? This will remove it from ${tag.totalCount} items.`)) {
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await tagService.deleteTag(tag.tag, appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Deleted from ${result.updated} items, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to delete tag');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleClose = () => {
    if (isReplacing) return;
    setSelectedTag('');
    setSearchTerm('');
    setEditedName('');
    setActiveTab('edit');
    setError('');
    onClose();
  };

  if (!isOpen || !tag) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Manage Tag: "{tag.tag}"</h2>
            <button
              onClick={handleClose}
              disabled={isReplacing}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'edit'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Edit Name
            </button>
            <button
              onClick={() => setActiveTab('replace')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'replace'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Replace with Existing
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium mb-2">Current Usage</h3>
            <div className="text-sm space-y-1">
              <div>Logos: {tag.logoCount}</div>
              <div>Project Names: {tag.projectNameCount}</div>
              <div className="font-medium">Total: {tag.totalCount} items</div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          {activeTab === 'edit' ? (
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Edit tag name
                </label>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Enter corrected tag name"
                  disabled={isReplacing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Fix typos or make small corrections to the tag name
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isReplacing}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isReplacing}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                
                <button
                  type="submit"
                  disabled={isReplacing || !editedName.trim() || editedName === tag?.tag}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {isReplacing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit2 size={16} />
                      Update Name
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleReplace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Replace with existing tag
                </label>
                
                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    placeholder="Search for existing tag..."
                    disabled={isReplacing}
                  />
                </div>

                {/* Tag suggestions */}
                {searchTerm && availableTags.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                    {availableTags.map((availableTag) => (
                      <button
                        key={availableTag.tag}
                        type="button"
                        onClick={() => {
                          setSelectedTag(availableTag.tag);
                          setSearchTerm(availableTag.tag);
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between ${
                          selectedTag === availableTag.tag ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                        disabled={isReplacing}
                      >
                        <div>
                          <span className="font-medium">{availableTag.tag}</span>
                          <div className="text-xs text-gray-500 mt-1">
                            Used in {availableTag.totalCount} items
                          </div>
                        </div>
                        {selectedTag === availableTag.tag && (
                          <div className="text-blue-600">✓</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected tag display */}
                {selectedTag && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <span className="text-sm text-blue-700">
                      Selected: <strong>{selectedTag}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTag('');
                        setSearchTerm('');
                      }}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      disabled={isReplacing}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Search and select an existing tag to replace "{tag.tag}" with
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isReplacing}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isReplacing}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                
                <button
                  type="submit"
                  disabled={isReplacing || !selectedTag.trim()}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {isReplacing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Replace size={16} />
                      Replace
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminTagManagement: React.FC = () => {
  const { appUser, permissions } = useAuth();
  const [tags, setTags] = useState<TagWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagWithCounts | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permissions.canManageUsers) {
      loadTags();
    }
  }, [permissions.canManageUsers]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const fetchedTags = await tagService.getAllTagsWithCounts();
      setTags(fetchedTags);
    } catch (error) {
      console.error('Failed to load tags:', error);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceTag = (tag: TagWithCounts) => {
    setSelectedTag(tag);
    setShowReplaceModal(true);
  };

  const handleSuccess = () => {
    loadTags();
  };

  const filteredTags = tags.filter(tag => 
    tag.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!permissions.canManageUsers) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to manage tags.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading tags...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Tags size={28} className="text-primary" />
            Tag Management
          </h2>
          <p className="text-gray-600 mt-1">Manage tags across logos and project names</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Tags</div>
          <div className="text-2xl font-bold text-primary">{tags.length}</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border rounded-lg w-full"
        />
      </div>

      {/* Tags Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Tags ({filteredTags.length})</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tag Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Logos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Names
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Usage
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTags.map((tag) => (
                <tr key={tag.tag} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {tag.tag}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {tag.logoCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {tag.projectNameCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{tag.totalCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleReplaceTag(tag)}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-xs hover:bg-gray-200 flex items-center gap-1 ml-auto"
                    >
                      <Edit2 size={12} />
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Replace Tag Modal */}
      <ReplaceTagModal
        isOpen={showReplaceModal}
        tag={selectedTag}
        allTags={tags}
        onClose={() => {
          setShowReplaceModal(false);
          setSelectedTag(null);
        }}
        onSuccess={handleSuccess}
      />
    </div>
  );
};